/**
 * The conversation, held in this tab's memory and nowhere else (DESIGN.md §10.8: it can contain
 * health data, so never `localStorage` or `sessionStorage`). It lives outside React on purpose: the
 * planner page unmounts whenever the person follows a «Fet» link or goes to Preferences, and the
 * conversation must still be there when they come back. A reload clears it; so do logging out and
 * another user.
 */
import type { AssistantFailureKind } from "@/logic/assistant";
import type { WeeklyMenuStructure } from "@/types";
import type { AssistantHistorySourceEntry } from "./historyWindow";
import type { ThreadItem } from "./thread";

export interface ConversationState {
  /** Whose conversation this is. A different user starts from nothing. */
  ownerId: number | string | null;
  items: ThreadItem[];
  changedCourses: ReadonlySet<string>;
  draft: string;
  /** When the running turn started (ms), or null. Here, not in a component, so a page that remounts
   *  mid-turn still locks the planner and keeps counting «Hi penso…» from the real start. */
  turnStartedAt: number | null;
  /** The per-minute limit's end (ms), or null. */
  rateLockedUntil: number | null;
  /** For the rest of this session: the day's cap, or the server's switch being off. */
  block: "daily" | "disabled" | null;
  /** The panel as the person left it, so coming back to the planner finds it the same. */
  panelOpen: boolean;
  panelTall: boolean;
  /** How many items the person has seen with the panel open: more than that is «Resposta nova». */
  seenCount: number;
  /** The person left the planner page (not just its loading screen): coming back, focus returns. */
  leftPlanner: boolean;
}

/**
 * A turn that may have changed the week and whose outcome we could not yet tell: a timeout that
 * found nothing so far, or a failure whose re-read failed too. README §6: look again later, when the
 * connection comes back, and before the next turn — and only then say what changed, or that nothing did.
 */
export interface PendingCheck {
  epoch: number;
  kind: AssistantFailureKind;
  weekStart: string;
  message: string;
  /** The turn's first system line, which may carry «Torna-ho a provar». */
  lineId: string;
  /** The model's note for that turn, rewritten once we know what changed. */
  note: AssistantHistorySourceEntry;
  /** The server's week as last read for this turn (or its «before» if that read failed). */
  baseline: WeeklyMenuStructure;
  /** The planner's write count then. While it has not moved, any difference from `baseline` is the
   *  chat's; once the planner has written, only a difference from what it shows can be. */
  writesAtSet: number;
  /** How many timed late looks are left. The planner mounting or finishing a write, the connection
   *  coming back and the next turn look too. */
  attemptsLeft: number;
  /** Set when a look settles it: whether that look found the turn's change. */
  outcome?: "changed" | "none";
}

const EMPTY: ConversationState = {
  ownerId: null,
  items: [],
  changedCourses: new Set(),
  draft: "",
  turnStartedAt: null,
  rateLockedUntil: null,
  block: null,
  panelOpen: false,
  panelTall: false,
  seenCount: 0,
  leftPlanner: false,
};

let state: ConversationState = EMPTY;
/** What the model is told, built as the turns go. Mutable and not rendered, so not in `state`. */
let history: AssistantHistorySourceEntry[] = [];
let pending: PendingCheck | null = null;
let lastId = 0;
/**
 * Which conversation this is. Bumped by another owner, logging out and «Comença de nou». Anything
 * that started under an older epoch — a turn still waiting, an offer still saving, a late look —
 * finds it moved when it lands, and says nothing.
 */
let epoch = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

function startOver(next: ConversationState): void {
  history = [];
  pending = null;
  epoch += 1;
  state = next;
  notify();
}

export const conversationStore = {
  get: (): ConversationState => state,
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  update(change: Partial<ConversationState> | ((current: ConversationState) => Partial<ConversationState>)): void {
    const patch = typeof change === "function" ? change(state) : change;
    state = { ...state, ...patch };
    notify();
  },
  /**
   * Starts over for this owner, unless the conversation is already theirs. No owner (the session is
   * being checked, or has just expired) keeps it: the same person signing back in finds their thread
   * (README §6, 401: «el fil es manté»). Logging out is `reset`.
   */
  claim(ownerId: number | string | null): void {
    if (ownerId === null || state.ownerId === ownerId) return;
    startOver({ ...EMPTY, ownerId });
  },
  /** Logging out: nothing of this person's stays in memory. */
  reset(): void {
    startOver(EMPTY);
  },
  /** «Comença de nou»: the thread goes; what the server said about this session stays. */
  clear(): void {
    const { ownerId, block, rateLockedUntil, panelOpen, panelTall } = state;
    // The panel stays as it is: the person is in it, pressing «Comença de nou».
    startOver({ ...EMPTY, ownerId, block, rateLockedUntil, panelOpen, panelTall });
  },
  epoch: (): number => epoch,
  isEpoch: (turnEpoch: number): boolean => turnEpoch === epoch,
  history: (): AssistantHistorySourceEntry[] => history,
  pending: (): PendingCheck | null => pending,
  setPending(check: PendingCheck | null): void {
    pending = check;
  },
  nextId: (): string => `item-${(lastId += 1)}`,
};
